import { useState, useCallback } from 'react';
import moment from 'moment';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { useQuoteConfigStore } from 'pages/auto-rfq/store/rfqConfig';
import type { QuoteDTO } from '@setvi/shared/services';
import { TabType } from '../steps/prepared-by';
import { getCompanyData } from '../utils';
import type { QuoteDetailsFormData } from '../types';

export const useQuoteDetailsForm = (quote: QuoteDTO) => {
  const { user } = useAppContext();
  const { showManufacturerDetails } = useQuoteConfigStore();
  const companyData = getCompanyData(user?.CompanyID);

  const otherSalesRep = quote?.previewInfo?.preparedBy?.usedForOtherSalesRep;
  const initPreparedBy = quote?.previewInfo?.preparedBy;

  const [formData, setFormData] = useState<QuoteDetailsFormData>({
    preparedFor: {
      companyName: quote?.previewInfo?.preparedFor?.companyName || '',
      address: quote?.previewInfo?.preparedFor?.companyAddress || '',
      phoneNumber: quote?.previewInfo?.preparedFor?.companyPhoneNumber || ''
    },
    preparedBy: {
      file: null,
      type: otherSalesRep ? TabType.OTHER : TabType.PROFILE,
      companyName: companyData.name,
      companyAddress: companyData.address,
      companyRole: user?.CompanyRole || '',
      usedForOtherSalesRep: otherSalesRep,
      profileTabCache: {
        fullName: user?.FirstName ? `${user?.FirstName} ${user?.LastName}` : '',
        firstName: user?.FirstName || '',
        lastName: user?.LastName || '',
        email: user?.Email || '',
        contactNumber: user?.ContactNumber || '',
        photo: user?.Photo || ''
      },
      otherTabCache: {
        fullName: otherSalesRep ? initPreparedBy?.fullName : '',
        firstName: otherSalesRep ? initPreparedBy?.firstName : '',
        lastName: otherSalesRep ? initPreparedBy?.lastName : '',
        email: otherSalesRep ? initPreparedBy?.email : '',
        contactNumber: otherSalesRep ? initPreparedBy?.phoneNumber : '',
        photo: otherSalesRep ? initPreparedBy?.photo : ''
      }
    },
    expirationDate: quote?.validUntil
      ? new Date(quote.validUntil.toString().split('T')[0])
      : moment().add(30, 'days').toDate(),
    overallNote: quote?.customerNote || '',
    quoteDisplay: {
      showManufacturerDetails:
        quote?.previewInfo?.showManufacturerDetails ?? showManufacturerDetails,
      setAsDefault: false
    }
  });

  const [isExpired, setIsExpired] = useState(!!quote?.isExpired);

  const currentPreparedData =
    formData?.preparedBy?.type === TabType.OTHER
      ? formData?.preparedBy?.otherTabCache
      : formData?.preparedBy?.profileTabCache;

  const updateFormData = useCallback((stepId: string, data: any) => {
    setFormData(prev => ({
      ...prev,
      [stepId]: data
    }));
  }, []);

  return {
    formData,
    setFormData,
    currentPreparedData,
    updateFormData,
    isExpired,
    setIsExpired
  };
};
